import React, { useState, useEffect } from 'react';
import { MarqueeStyle } from './style';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import axios from 'axios';
import packageJson from '../../../package.json';
import { getPlatform } from '@/utils/platform';

interface UpdateInfo {
	version: string;
	releaseNotes?: string;
	releaseName?: string;
}

interface AndroidUpdateInfo {
	latestVersion: string;
	apkDownloadUrl: string;
	updateDesc?: string;
}

interface DownloadProgress {
	percent: number;
	transferred: number;
	total: number;
}

const App: React.FC = () => {
	const [showModal, setShowModal] = useState(false);
	const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
	const [downloadProgress, setDownloadProgress] = useState(0);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isDownloaded, setIsDownloaded] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');

	const isDev = import.meta.env.DEV;
	const [platform, setPlatform] = useState<'electron' | 'android' | 'browser'>('browser');
	const [androidUpdateInfo, setAndroidUpdateInfo] = useState<AndroidUpdateInfo | null>(null);
	const androidCurrentVersion = packageJson.version;
	// 修复1：更可靠的平台判断
	useEffect(() => {
		setPlatform(getPlatform());
	}, [isDev]);

	// 修复2：Electron主动触发更新检测 + 【新增】通知父组件有新版本
	useEffect(() => {
		// 非Electron平台直接返回
		if (isDev || platform !== 'electron') return;

		// 主动触发Electron更新检测
		const triggerElectronUpdateCheck = async () => {
			try {
				const electronIpc = (window as any).electronIpc as {
					sendUpdateCommand: (command: string) => void;
					onUpdateEvent: (channel: string, callback: (...args: any[]) => void) => void;
					removeUpdateListener: (channel: string) => void;
					getAppVersion: () => Promise<string>;
				};

				// 检查IPC对象是否存在
				if (!electronIpc) {
					throw new Error('Electron IPC通信对象不存在');
				}

				console.log('主动触发Electron更新检测');
				// 发送检测更新的指令给主进程
				electronIpc.sendUpdateCommand('check_update');

				// 【新增】获取Electron版本号
				const version = await electronIpc.getAppVersion();
				console.log('Electron应用版本：', version);
			} catch (err) {
				const errorMsg = `Electron更新检测初始化失败：${(err as Error).message}`;
				console.error(errorMsg);
				setErrorMsg(errorMsg);
				setShowModal(true);
			}
		};

		triggerElectronUpdateCheck();
	}, [isDev, platform]);



	useEffect(() => {
		if (isDev || platform !== 'android' || !androidCurrentVersion) return;
		checkAndroidUpdate();
	}, [isDev, platform, androidCurrentVersion]);

	useEffect(() => {
		if (isDev || platform !== 'android') return;

		const handleAppActive = async () => {
			console.log('App从后台切到前台，触发更新检测');
			if (androidCurrentVersion) {
				await checkAndroidUpdate();
			}
		};

		CapacitorApp.addListener('appStateChange', (state) => {
			if (state.isActive) {
				handleAppActive();
			}
		});

		return () => {
			CapacitorApp.removeAllListeners();
		};
	}, [isDev, platform, androidCurrentVersion]);

	// 修复3：重构Electron事件监听逻辑 + 【新增】通知父组件有新版本
	useEffect(() => {
		if (isDev || platform !== 'electron') return;

		const electronIpc = (window as any).electronIpc as {
			onUpdateEvent: (channel: string, callback: (...args: any[]) => void) => void;
			removeUpdateListener: (channel: string) => void;
		};

		if (!electronIpc) {
			const errorMsg = 'Electron IPC通信对象未初始化，无法监听更新事件';
			console.error(errorMsg);
			setErrorMsg(errorMsg);
			setShowModal(true);
			return;
		}

		console.log('开始监听Electron更新事件');

		// 更新可用
		const handleUpdateAvailable = (info: UpdateInfo) => {
			console.log('Electron检测到新版本：', info);
			setUpdateInfo(info);
			setShowModal(true); // 【关键】检测到更新自动弹框
			setErrorMsg('');
			// 【新增】通知父组件（Header）有新版本
			window.dispatchEvent(new CustomEvent('has_new_version', { detail: true }));
		};

		// 无更新
		const handleUpdateNotAvailable = () => {
			console.log('Electron当前已是最新版本');
			setErrorMsg('当前已是最新版本');
			setShowModal(true);
			// 通知父组件无新版本
			window.dispatchEvent(new CustomEvent('has_new_version', { detail: false }));
		};

		// 下载进度
		const handleDownloadProgress = (progress: DownloadProgress) => {
			console.log('Electron更新下载进度：', progress.percent);
			setDownloadProgress(Math.round(progress.percent));
			setIsDownloading(true);
		};

		// 下载完成
		const handleUpdateDownloaded = () => {
			console.log('Electron更新下载完成');
			setIsDownloading(false);
			setIsDownloaded(true);
		};

		// 更新错误
		const handleUpdateError = (msg: string) => {
			console.error('Electron更新错误：', msg);
			setErrorMsg(msg);
			setIsDownloading(false);
			setShowModal(true);
		};

		// 注册事件监听
		electronIpc.onUpdateEvent('update_available', handleUpdateAvailable);
		electronIpc.onUpdateEvent('update_not_available', handleUpdateNotAvailable);
		electronIpc.onUpdateEvent('download_progress', handleDownloadProgress);
		electronIpc.onUpdateEvent('update_downloaded', handleUpdateDownloaded);
		electronIpc.onUpdateEvent('update_error', handleUpdateError);

		// 清理函数
		return () => {
			console.log('移除Electron更新事件监听');
			electronIpc.removeUpdateListener('update_available');
			electronIpc.removeUpdateListener('update_not_available');
			electronIpc.removeUpdateListener('download_progress');
			electronIpc.removeUpdateListener('update_downloaded');
			electronIpc.removeUpdateListener('update_error');
		};
	}, [isDev, platform]);

	const checkAndroidUpdate = async () => {
		if (isDev || platform !== 'android') return;
		try {
			const response = await axios.get('https://down.gtokentool.com/version.json', {
				headers: { 'Cache-Control': 'no-cache' },
				validateStatus: (status) => status >= 200 && status < 300,
				timeout: 10000,
			});

			const latestData = response.data;
			const androidLatestInfo = latestData.data;

			if (!androidLatestInfo) throw new Error('未找到安卓端更新信息');
			if (androidLatestInfo.latestVersion !== androidCurrentVersion) {
				setAndroidUpdateInfo(androidLatestInfo);
				setShowModal(true);
				setErrorMsg(androidCurrentVersion + androidLatestInfo.latestVersion);
			}
		} catch (err) {
			let errorMsg = '检测更新失败，未知错误';
			if (axios.isAxiosError(err)) {
				if (err.response) {
					const status = err.response.status;
					errorMsg = status === 404 ? '检测更新失败，未找到版本配置文件' :
						status === 500 ? '检测更新失败，服务器内部错误' :
							`HTTP请求失败，状态码：${status}`;
				} else if (err.request) {
					errorMsg = '检测更新失败，请求已发送但未收到服务器响应';
				} else {
					errorMsg = `检测更新失败：${err.message}`;
				}
			} else if (err instanceof Error) {
				errorMsg = `检测更新失败：${err.message}`;
			}

			console.error(errorMsg);
			if (platform === 'android') {
				setErrorMsg(errorMsg);
				setShowModal(true);
			}
		}
	};

	// 修复4：完善handleDownload的Electron IPC调用错误处理
	const handleLater = () => {
		setShowModal(false);
		if (!isDev && platform === 'electron') {
			try {
				(window as any).electronIpc.sendUpdateCommand('check_update_later');
			} catch (err) {
				console.error('发送稍后更新指令失败：', err);
			}
		}
	};

	const handleDownload = () => {
		if (isDev) return;

		if (platform === 'electron') {
			try {
				setIsDownloading(true);
				(window as any).electronIpc.sendUpdateCommand('download_update');
			} catch (err) {
				console.error('发送下载更新指令失败：', err);
				setErrorMsg('下载更新失败：IPC通信异常');
				setIsDownloading(false);
				setShowModal(true);
			}
		} else if (platform === 'android' && androidUpdateInfo) {
			openAndroidDownloadUrl(androidUpdateInfo.apkDownloadUrl);
			setShowModal(false);
		}
	};

	const handleInstall = () => {
		if (!isDev && platform === 'electron') {
			try {
				(window as any).electronIpc.sendUpdateCommand('install_update');
			} catch (err) {
				console.error('发送安装更新指令失败：', err);
				setErrorMsg('安装更新失败：IPC通信异常');
				setShowModal(true);
			}
		}
	};

	const openAndroidDownloadUrl = async (url: string) => {
		if (isDev || platform !== 'android') return;
		try {
			await Browser.open({
				url,
				windowName: '_blank',
				toolbarColor: '#280D5F',
				presentationStyle: 'fullscreen'
			});
		} catch (err) {
			console.error('安卓端打开下载链接失败：', err);
			const { Clipboard } = await import('@capacitor/clipboard');
			await Clipboard.write({ string: url });
			setErrorMsg(`打开下载链接失败，已复制链接到剪贴板：\n${url}`);
		}
	};

	const renderAndroidUpdateContent = () => {
		if (isDev || platform !== 'android') return null;
		return (
			<div className="update-tips">
				<p>应用已发布新版本，是否立即升级？</p>
				<div className="update-log">
					<span>更新日志：</span>
					<p>{androidUpdateInfo?.updateDesc || '优化应用性能，修复已知问题'}</p>
				</div>
			</div>
		);
	};

	// 【修改】移除platform === 'browser'的判断，确保Electron能显示弹框
	if (isDev || !showModal) return null;

	return (
		<MarqueeStyle>
			<div className="update-modal-mask">
				<div className="update-modal">
					<div className="update-modal-header">
						<h3>📢 发现新版本</h3>
						{platform === 'electron' && updateInfo && (
							<p className="version-text">v{updateInfo.version}</p>
						)}
						{platform === 'android' && androidUpdateInfo && (
							<p className="version-text">v{androidUpdateInfo.latestVersion}</p>
						)}
					</div>

					<div className="update-modal-content">
						{/* 优先显示错误信息 */}
						{errorMsg && <p className="error-tip">{errorMsg}</p>}

						{platform === 'electron' ? (
							<>
								{!isDownloading && !isDownloaded ? (
									<div className="update-tips">
										<p>应用已发布新版本，是否立即升级？</p>
										<div className="update-log">
											<span>更新日志：</span>
											<p>{updateInfo?.releaseNotes || '优化应用性能，修复已知问题'}</p>
										</div>
									</div>
								) : isDownloading ? (
									<div className="download-progress-wrap">
										<p>正在下载更新：{downloadProgress}%</p>
										<div className="progress-bar">
											<div
												className="progress-fill"
												style={{ width: `${downloadProgress}%` }}
											></div>
										</div>
									</div>
								) : (
									<div className="download-complete">
										<p>✅ 更新已下载完成</p>
										<p>点击「立即安装」后应用将自动重启</p>
									</div>
								)}
							</>
						) : (
							<>
								{!isDownloading && !isDownloaded && renderAndroidUpdateContent()}
							</>
						)}
					</div>

					<div className="update-modal-footer">
						{platform === 'electron' ? (
							<>
								{!isDownloading && !isDownloaded ? (
									<>
										<button className="btn btn-later" onClick={handleLater}>
											稍后升级
										</button>
										<button className="btn btn-primary" onClick={handleDownload}>
											立即升级
										</button>
									</>
								) : isDownloading ? (
									<button className="btn btn-disabled" disabled>
										下载中... {downloadProgress}%
									</button>
								) : (
									<>
										<button className="btn btn-later" onClick={handleLater}>
											稍后安装
										</button>
										<button className="btn btn-primary" onClick={handleInstall}>
											立即安装
										</button>
									</>
								)}
							</>
						) : (
							<>
								{!isDownloading && !isDownloaded ? (
									<>
										<button className="btn btn-later" onClick={handleLater}>
											稍后升级
										</button>
										<button className="btn btn-primary" onClick={handleDownload}>
											立即升级
										</button>
									</>
								) : (
									<button className="btn btn-disabled" disabled>
										已打开下载链接
									</button>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</MarqueeStyle>
	);
};

export default App;